// Copyright 2020 TOKU Systems Inc.

import React, { useState, useEffect } from 'react';
import { List } from 'immutable';
import { Signal } from '@equestria/spike';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  FormControlLabel,
  Radio,
  DialogActions,
  Button,
} from '@material-ui/core';
import illumass from '../../services/illumass';
import * as _ from 'lodash';
import { useSnackbar } from 'notistack';

interface SignalPickerProps {
  open: boolean;
  originId: string;
  onSignalPicked: (pickedSignal: Signal) => void;
  onClose: () => void;
}

export default function SignalPicker(props: SignalPickerProps): JSX.Element {
  const [originSignals, setOriginSignals] = useState<List<Signal>>(List());
  const [signalsChecked, setSignalsChecked] = useState<List<boolean>>(List());
  const [initializationStarted, setInitializationStarted] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { originId } = props;

  useEffect(() => {
    if (!initializationStarted) {
      setInitializationStarted(true);

      const q = _.set({}, ['query', 'term', 'origin.id', 'value'], originId);
      illumass.signal
        .search(q, {
          size: 1000,
        })
        .then((searchResult) => {
          if (searchResult.fetched > 0) {
            setSignalsChecked(List(searchResult.hits).map(() => false));
            setOriginSignals(List(searchResult.hits).map((hit) => hit._source));
          } else {
            setSignalsChecked(List());
            setOriginSignals(List());
          }
        });
    }
  }, [initializationStarted, originId]);

  const handleSignalCheckChange = (signalId: string): void => {
    const signalIndex = originSignals.findIndex((s) => s.id === signalId);
    if (signalIndex >= 0) {
      setSignalsChecked(signalsChecked.map((_s, key) => key === signalIndex));
    } else {
      enqueueSnackbar(`Unable to pick signal ${signalId}`, {
        variant: 'warning',
      });
    }
  };

  const handleApplyClick = (): void => {
    const checkedIndex = signalsChecked.findIndex((x) => x);
    if (checkedIndex >= 0) {
      const signalPicked = originSignals.get(checkedIndex);
      if (signalPicked !== undefined) {
        props.onSignalPicked(signalPicked);
      } else {
        enqueueSnackbar(`No signal at index ${checkedIndex}`, {
          variant: 'error',
        });
      }
    } else {
      enqueueSnackbar('No signal picked', { variant: 'warning' });
    }
  };

  return (
    <Dialog open={props.open} onClose={props.onClose} scroll="paper" fullWidth>
      <DialogTitle>Signals</DialogTitle>
      <DialogContent dividers>
        <FormControl component="fieldset">
          {originSignals.map((s, key) => {
            return (
              <FormControlLabel
                key={s.id ?? key.toString()}
                checked={signalsChecked.get(key) ?? false}
                control={<Radio />}
                onChange={(): void =>
                  handleSignalCheckChange(s.id ?? key.toString())
                }
                label={s.name ?? '???'}
              />
            );
          })}
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>Cancel</Button>
        <Button autoFocus onClick={handleApplyClick}>
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}
